import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState, useEffect } from 'react'
import { useCalendarState } from '@/hooks/useCalendarState'
import { useWorkingHours } from '@/hooks/useWorkingHours'
import {
  CalendarHeader,
  CalendarNavigation,
  CalendarGrid,
  AppointmentDetailsDialog
} from '@/components/calendar'
import { AppointmentFormDialog } from '@/components/calendar/AppointmentFormDialog'
import { PageContainer, PageCard, PageHeader, PageContent } from '@/components/ui/page'

export const Route = createFileRoute('/bookings')({
  component: Bookings,
  errorComponent: RouteErrorFallback,
  validateSearch: (search: Record<string, unknown>) => ({
    id: (search.id as string) || undefined,
    calendarId: (search.calendarId as string) || undefined,
    date: (search.date as string) || undefined,
  }),
})

function Bookings() {
  const { id: searchId, calendarId: searchCalendarId, date: searchDate } = Route.useSearch()
  const [dragError, setDragError] = useState<string | null>(null)
  const [deepLinkPending, setDeepLinkPending] = useState<string | null>(searchId ?? null)

  const {
    viewMode,
    setViewMode,
    viewModeOptions,
    currentDate,
    selectedAppointment,
    setSelectedAppointment,
    selectedCalendarId,
    setSelectedCalendarId,
    editingAppointment,
    isEditDialogOpen,
    isCreateDialogOpen,
    createDialogInitialTime,
    createDialogInitialDuration,
    
    // Search-related state and actions
    isSearchEnabled,
    handleSearchResultSelect,
    enableSearch,
    disableSearch,
    isAppointmentHighlighted,
    
    calendars,
    appointments,
    appointmentColors,
    
    isLoadingCalendars,
    isLoadingEventTypes,
    isLoading,
    
    calendarsError,
    eventTypesError,
    error,
    
    setCurrentDate,
    navigateDate,
    goToToday,
    handleEditAppointment,
    handleEditDialogClose,
    handleCreateAppointment,
    handleCreateDialogClose
  } = useCalendarState()

  // Deep-link: set calendar and date from URL params
  useEffect(() => {
    if (searchCalendarId) {
      setSelectedCalendarId(searchCalendarId)
    }
    if (searchDate) {
      setCurrentDate(new Date(searchDate + 'T00:00:00'))
    }
  }, [searchCalendarId, searchDate, setSelectedCalendarId, setCurrentDate])

  // Deep-link: open appointment dialog once data loads
  useEffect(() => {
    if (deepLinkPending && appointments.length > 0) {
      const found = appointments.find(apt => apt.id === deepLinkPending)
      if (found) {
        setSelectedAppointment(found)
        setDeepLinkPending(null)
      }
    }
  }, [deepLinkPending, appointments, setSelectedAppointment])

  // Enable search functionality on component mount
  useEffect(() => {
    enableSearch()
    return () => {
      disableSearch()
    }
  }, [enableSearch, disableSearch])

  const handleDragError = (errorMessage: string) => {
    setDragError(errorMessage)
    // Clear error after 5 seconds
    setTimeout(() => setDragError(null), 5000)
  }

  const handleCreateDrag = (startTime: Date, duration: number) => {
    handleCreateAppointment(startTime, duration)
  }

  // Load working hours for the selected calendar
  const { data: workingHours, isLoading: isLoadingWorkingHours } = useWorkingHours(selectedCalendarId)

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <CalendarHeader
            title="Buchungen"
            subtitle="Termine und Kalendereinträge verwalten"
            calendars={calendars}
            selectedCalendarId={selectedCalendarId}
            onCalendarChange={setSelectedCalendarId}
            onCreateClick={() => handleCreateAppointment()}
            enableSearch={isSearchEnabled}
            onSearchResultSelect={handleSearchResultSelect}
          />
        </PageHeader>

        <PageContent>
          <div className="flex flex-col h-full">
            <div className="p-4 pb-3 md:p-6 md:pb-4">
              <CalendarNavigation
                viewMode={viewMode}
                viewModeOptions={viewModeOptions}
                currentDate={currentDate}
                onViewModeChange={setViewMode}
                onNavigate={navigateDate}
                onGoToToday={goToToday}
              />
            </div>

            {/* Mobil scrollt das Raster (bzw. die Seite) mit, ab md bleibt der Innen-Clip */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 md:overflow-hidden md:px-6 md:pb-6">
              <CalendarGrid
                viewMode={viewMode}
                currentDate={currentDate}
                appointments={appointments}
                appointmentColors={appointmentColors}
                onAppointmentClick={setSelectedAppointment}
                isLoadingCalendars={isLoadingCalendars}
                isLoadingEventTypes={isLoadingEventTypes}
                isLoading={isLoading || isLoadingWorkingHours}
                calendarsError={calendarsError}
                eventTypesError={eventTypesError}
                error={error}
                calendars={calendars}
                selectedCalendarId={selectedCalendarId}
                enableDragDrop={true}
                onDragError={handleDragError}
                onCreateDrag={handleCreateDrag}
                workingHours={workingHours}
                isAppointmentHighlighted={isAppointmentHighlighted}
              />
            </div>
          </div>
        </PageContent>
      </PageCard>

      <AppointmentDetailsDialog
        appointment={selectedAppointment}
        open={!!selectedAppointment}
        onOpenChange={(open: boolean) => !open && setSelectedAppointment(null)}
        onEditClick={handleEditAppointment}
      />

      <AppointmentFormDialog
        mode="edit"
        appointment={editingAppointment}
        open={isEditDialogOpen}
        onOpenChange={handleEditDialogClose}
      />

      <AppointmentFormDialog
        mode="create"
        open={isCreateDialogOpen}
        onOpenChange={handleCreateDialogClose}
        selectedCalendarId={selectedCalendarId}
        initialStartTime={createDialogInitialTime || undefined}
        initialDuration={createDialogInitialDuration}
      />

      {/* Simple error toast for drag & drop */}
      {dragError && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {dragError}
        </div>
      )}
    </PageContainer>
  )
}